import { Dialog, Disclosure, Transition } from "@headlessui/react";
import { Fragment, useContext, createContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaArrowRightLong } from "react-icons/fa6";
import { GoDotFill } from "react-icons/go";
import { IoMdMenu } from "react-icons/io";
import { RxCross2 } from "react-icons/rx";
import { LuLayoutDashboard, LuRoute, LuSearch, LuGauge, LuMessagesSquare, LuHouse, LuSparkles, LuPlus, LuStore } from "react-icons/lu";
import { isApp } from "../../../native/platform";
import MobileBackButton from "../../common/MobileBackButton";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import socket from "../../../config/ChatSocket/socket";
import { notificationListener } from "../../../config/Firebase/FirebaseAuth";
import { removePropData } from "../../../models/string.model";
import LoginModal from "../../common/Modal/LoginModal";
import "./style.scss";
import ApiClient from "../../../methods/api/apiClient";
import { isDebugMockUser, isGuestMode, getGuestSessionId } from "../../../methods/guestMode";
import { openLoginModal } from "../../../methods/openLoginModal";
import UpgradePlan from "../../common/Modal/UpgradePlan";
import { openUpgradeModal } from "../../../models/upgradeModal";
import { ChevronUpIcon } from "@heroicons/react/20/solid";
import { login_success, logout } from "../../../actions/user";
import { active_plan_success } from "../../../actions/activePlan";
import Sidebar from "../sidebar";
import LanguageSwitcher from "../../../LanguageSwitcher";
import Header from "../header";
import OnboardingCompletionModal from "../../onboarding/OnboardingCompletionModal";
import { useOnboardingCompletion } from "../../onboarding/useOnboardingCompletion";
import TrialBanner from "../TrialBanner";

// Context permettant de détecter qu'on est déjà à l'intérieur d'un PageLayout
// (le PageLayout racine est mont\u00e9 dans App.tsx au-dessus du Router et ne d\u00e9monte
//  jamais ; les PageLayout imbriqu\u00e9s dans les pages se contentent de rendre
//  leurs enfants pour \u00e9viter le clignotement).
const PageLayoutContext = createContext(false);

const detectTablet = () => {
  if (typeof window === "undefined") return false;
  const w = window.innerWidth || 0;
  if (w < 768) return false;
  // iPad / tablette en app native (Capacitor) : l'app est déjà tablette.
  if (isApp()) return w <= 1400;
  // iPad Safari / tablette tactile : pointeur grossier + multi-touch.
  try {
    const coarse = typeof window.matchMedia === "function" && window.matchMedia("(pointer: coarse)").matches;
    const touch = typeof navigator !== "undefined" && (navigator.maxTouchPoints || 0) > 1;
    return w <= 1400 && coarse && touch;
  } catch (e) {
    return false;
  }
};

const PageLayout = ({ children }) => {
  const insideLayout = useContext(PageLayoutContext);
  if (insideLayout) {
    // D\u00e9j\u00e0 un PageLayout racine au-dessus -> juste passer le contenu.
    return <>{children}</>;
  }
  return <PageLayoutContext.Provider value={true}><PageLayoutInner>{children}</PageLayoutInner></PageLayoutContext.Provider>;
};

const PageLayoutInner = ({ children }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    try {
      const saved = localStorage.getItem("sidebar_open");
      return saved !== null ? JSON.parse(saved) : true;
    } catch (e) {
      return true;
    }
  });
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  // Responsive breakpoints: < 768px → mobile (hamburger), 768–1023px → auto-collapsed
  const [isMobileView, setIsMobileView] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );
  const [isTablet, setIsTablet] = useState(detectTablet);
  const [projectData, setProjectData] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const activePlan = useSelector((state) => state.activePlan);
  const [propertyTotal, setpropertyTotal] = useState(0);
  const [propertyLoader, setpropertyLoader] = useState(false);
  const [planModal, setplanModal] = useState(false);
  const pathname = location.pathname;
  const user = useSelector((state) => state.user);
  const [loginModal, setloginModal] = useState(false);
  const [notLength, setNotLength] = useState(0);
  const [chatLength, setChatLength] = useState(0);
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const [isInChatPage, setIsInChatPage] = useState(false);
  const dispatch = useDispatch();
  const menuRef = useRef("");

  // Check if user is logged in or in guest mode
  const isLoggedIn = user?.loggedIn;
  const isGuest = user?.isGuest || isGuestMode();

  // Ouverture globale de la modale de connexion (depuis n'importe quelle page) :
  // window.dispatchEvent(new Event('anyhomes:open-login')).
  useEffect(() => {
    const open = () => setloginModal(true);
    window.addEventListener("anyhomes:open-login", open);
    return () => window.removeEventListener("anyhomes:open-login", open);
  }, []);

  // Onboarding completion celebration — shown at most once, on page load only
  const { showModal: showCompletionModal, markCelebrationSeen } = useOnboardingCompletion();

  // Check if we should exclude sidebar from current route
  const excludeSidebarRoutes = ["/login", "/signup", "/forgotpassword", "/reset-password", "/otpverify", "/change-password", "/reset-email", "/reset-new-email", "/signup/pro", "/phone-number", "/activation-pro"];
  const shouldShowSidebar = (isLoggedIn || isGuest) && !excludeSidebarRoutes.some(route => pathname.startsWith(route));

  // sidebar state is now initialized from localStorage synchronously to avoid
  // layout changes after first render

  // Toggle sidebar and save state
  const toggleSidebar = () => {
    const newState = !isSidebarOpen;
    setIsSidebarOpen(newState);
    localStorage.setItem("sidebar_open", JSON.stringify(newState));
  };

  // Close mobile sidebar on route change
  useEffect(() => {
    setIsMobileSidebarOpen(false);
  }, [pathname]);

  // Réaffiche le header à chaque navigation. Certaines pages (ex. Learning
  // Center, filtres de recherche) masquent le header au scroll en écrivant
  // `--header-ty` sur <html>. Sans remise à zéro, cette variable « colle » à la
  // page suivante : le header reste translaté hors écran (invisible) jusqu'à un
  // refresh — cas de /blog-own-detail et /video-own-detail.
  useEffect(() => {
    try {
      document.documentElement.style.setProperty("--header-ty", "translateY(0)");
    } catch (e) { /* ignore */ }
  }, [pathname]);

  // Responsive resize: auto-collapse on medium, detect mobile
  useEffect(() => {
    const handleResize = () => {
      const w = window.innerWidth;
      setIsMobileView(w < 768);
      setIsTablet(detectTablet());
      if (w < 768) {
        // Mobile: close desktop overlay if open
        setIsMobileSidebarOpen(false);
      } else if (w < 1024) {
        // Medium (768–1023px): auto-collapse sidebar, don't persist
        setIsSidebarOpen(false);
      } else {
        // Large (≥1024px): restore user preference
        const saved = localStorage.getItem('sidebar_open');
        setIsSidebarOpen(saved !== null ? JSON.parse(saved) : true);
      }
    };
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    handleResize();
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-collapse desktop sidebar on the /estimation page (préférence produit).
  // Pas de persistance localStorage : la préférence utilisateur est conservée
  // pour les autres pages, on ne fait que refermer visuellement à l'arrivée.
  useEffect(() => {
    if (pathname === "/estimation") {
      setIsSidebarOpen(false);
    }
    // « Rêvons plus grand » (formulaire + résultats) : referme la sidebar pour
    // laisser la place à l'expérience de projection.
    if (pathname.startsWith("/revons-plus-grand")) {
      setIsSidebarOpen(false);
    }
  }, [pathname]);

  function closeModal() {
    setIsOpen(false);
  }
  function openModal() {
    setIsOpen(true);
  }

  const Logout = () => {
    dispatch(logout());
    localStorage.removeItem("persist:admin-app");
    localStorage.removeItem("token");
    navigate("/login");
  };

  const notificationRead = () => {
    navigate("/notifications");
    socket.emit("mark-as-read-noti", {
      userId: user?.id || user?._id,
    });
  };

  const projectMenus = [
    {
      head: "",
      sub: [{ name: t("project.myProject"), url: "/project" }],
    },
    {
      head: t("project.searcherSpace"),
      sub: [
        { name: t("project.searchAlert"), url: "/serach-alert" },
        { name: t("project.propertiesFollowed"), url: "/followed-properties" },
        { name: t("project.interactedProperties"), url: "/properties?favourites=true" },
        { name: t("project.renterApplicationFile"), url: "/renter-file" },
        { name: t("project.buyerFile"), url: "/buyer-file" },
        {
          name: t("project.manageTransaction"),
          url: "/real-estate-transaction-searcher",
        },
        {
          name: t("project.p2pEstimation"),
          url: "/estimation",
        },
      ],
    },
    {
      head: t("project.ownerSpace"),
      sub: [
        { name: t("project.myProperty"), url: "/my-properties" },
        { name: t("buttons.listProperty"), url: "/property1" },
        { name: t("project.sellerFile"), url: "/seller-file" },
        {
          name: t("project.manageTransaction"),
          url: "/real-estate-transaction-owner",
        },
        {
          name: t("project.manageP2pEstimation"),
          url: "/social-estimation",
        },
      ],
    },
  ];

  const accountMenu = [
    {
      name: t('header.account'),
      title: t('header.account'),
      image: (
        <img src="/assets/img/header/account.png" className="w-[20px]" alt="" />
      ),
      url: "/profile/Account",
      menu: (
        <>
          <ul className="bg-white py-4 pe-4 ps-2 right-0 rounded-[10px] absolute w-[220px] shadow-md border-0 border-[#00000024]">
            {/* Langue — en haut du menu (le switch du header est masqué sur mobile) */}
            {isMobileView && (
              <li className="text-[#47525E] text-left my-1 text-[14px] flex items-center hover:text-[var(--brand,#976DD0)] transition-all duration-500 ease-in-out border-b border-gray-100 pb-2">
                <LanguageSwitcher />
              </li>
            )}
            {/* 1. Informations personnelles */}
            <li
              onClick={() => {
                navigate("/profile/Account");
              }}
              className="text-[#47525E] text-left font-normal cursor-pointer my-1 hover:text-[var(--brand,#976DD0)] transition-all duration-500 ease-in-out flex items-center group text-[14px]"
            >
              <GoDotFill className="flex group-hover:hidden me-2 w-[15px] transition-all duration-500 ease-in-out" />
              <FaArrowRightLong className="w-[15px] hidden group-hover:flex me-2 opacity-0 group-hover:opacity-100 transition-all duration-500 ease-in-out" />
              {t('header.personalInformation')}
            </li>            {/* Notifications */}
            <li
              onClick={() => {
                navigate("/profile/manage-notifications");
              }}
              className="text-[#47525E] text-left font-normal cursor-pointer my-1 hover:text-[var(--brand,#976DD0)] transition-all duration-500 ease-in-out flex items-center group text-[14px]"
            >
              <GoDotFill className="flex group-hover:hidden me-2 w-[15px] transition-all duration-500 ease-in-out" />
              <FaArrowRightLong className="w-[15px] hidden group-hover:flex me-2 opacity-0 group-hover:opacity-100 transition-all duration-500 ease-in-out" />
              {t('header.notifications')}
            </li>            {/* 6. Centre d’aide / Contact */}
            {user?.accountType === 'pro' && (
              <>
                <li
                  onClick={() => {
                    navigate(
                      user?.role === 'investor'
                        ? "/upgrade-plan?type=investor"
                        : "/upgrade-plan"
                    );
                  }}
                  className="text-[#47525E] text-left font-normal cursor-pointer my-1 hover:text-[var(--brand,#976DD0)] transition-all duration-500 ease-in-out flex items-center group text-[14px]"
                >
                  <GoDotFill className="flex group-hover:hidden me-2 w-[15px] transition-all duration-500 ease-in-out" />
                  <FaArrowRightLong className="w-[15px] hidden group-hover:flex me-2 opacity-0 group-hover:opacity-100 transition-all duration-500 ease-in-out" />
                  {t('header.upgradePlan')}
                </li>
                <li
                  onClick={() => {
                    navigate("/pro/payouts");
                  }}
                  className="text-[#47525E] text-left font-normal cursor-pointer my-1 hover:text-[var(--brand,#976DD0)] transition-all duration-500 ease-in-out flex items-center group text-[14px]"
                >
                  <GoDotFill className="flex group-hover:hidden me-2 w-[15px] transition-all duration-500 ease-in-out" />
                  <FaArrowRightLong className="w-[15px] hidden group-hover:flex me-2 opacity-0 group-hover:opacity-100 transition-all duration-500 ease-in-out" />
                  {t('header.payouts')}
                </li>
              </>
            )}
            {/* Abonnement & facturation — disponible pour tous les comptes (pro et particulier). */}
            <li
              onClick={() => {
                navigate("/billing");
              }}
              className="text-[#47525E] text-left font-normal cursor-pointer my-1 hover:text-[var(--brand,#976DD0)] transition-all duration-500 ease-in-out flex items-center group text-[14px]"
            >
              <GoDotFill className="flex group-hover:hidden me-2 w-[15px] transition-all duration-500 ease-in-out" />
              <FaArrowRightLong className="w-[15px] hidden group-hover:flex me-2 opacity-0 group-hover:opacity-100 transition-all duration-500 ease-in-out" />
              {t('header.subscriptionBilling')}
            </li>
            <li
              onClick={() => {
                navigate("/contact-us");
              }}
              className="text-[#47525E] text-left font-normal cursor-pointer my-1 hover:text-[var(--brand,#976DD0)] transition-all duration-500 ease-in-out flex items-center group text-[14px]"
            >
              <GoDotFill className="flex group-hover:hidden me-2 w-[15px] transition-all duration-500 ease-in-out" />
              <FaArrowRightLong className="w-[15px] hidden group-hover:flex me-2 opacity-0 group-hover:opacity-100 transition-all duration-500 ease-in-out" />
{t('header.helpCenter')}
              </li>
              {/* 7. Déconnexion */}
            <li
              onClick={() => {
                Logout()
              }}
              className="text-[#47525E] text-left font-normal cursor-pointer my-1 hover:text-[var(--brand,#976DD0)] transition-all duration-500 ease-in-out flex items-center group text-[14px]"
            >
              <GoDotFill className="flex group-hover:hidden me-2 w-[15px] transition-all duration-500 ease-in-out" />
              <FaArrowRightLong className="w-[15px] hidden group-hover:flex me-2 opacity-0 group-hover:opacity-100 transition-all duration-500 ease-in-out" />
              {t('header.logout')}
            </li>
          </ul>
        </>
      ),
    },
  ]

  const menus = [];  // Menus supprimés quand sidebar est affiché - À gérer via conditionalité


  const mobMenus = [
    {
      name: t("header.plans"),
      link: "/upgrade-plan",
      img: "/assets/img/header/bulb.png",
    },
    {
      name: t("header.marketInsights"),
      img: "/assets/img/header/home.png",
      menu: [
        {
          name: t("header.transactions"),
          link: "/past-transactions",
        },
        {
          name: t("header.realEstatePros"),
          link: "/real-estate-pros",
        },
        {
          name: t("header.buildingPermits"),
          link: "/building-permit",
        },
      ],
    },
    {
      name: t("header.innovativeServices"),
      link: "/real-estate-pros",
      img: "/assets/img/header/hands.png",
      menu: [
        {
          name: t("home.tabs.directory"),
          link: "/real-estate-transaction-owner",
        },
        {
          name: t("home.tabs.offMarket"),
          link: "/real-estate-transaction-owner",
        },
        {
          name: t("project.p2pEstimation"),
          link: "/real-estate-transaction-owner",
        },
        {
          name: t("header.transactionTool"),
          link: "/real-estate-transaction-owner",
        },
      ],
    },
    {
      name: t("header.realEstatePros"),
      link: "/real-estate-pros",
      img: "/assets/img/header/home.png",
    },
    {
      key: "myProject",
      name: t("project.myProject"),
      img: "/assets/img/header/home.png",
      menu: [
        {
          head: "",
          sub: [{ name: t("project.myProject"), url: "/project" }],
        },
        {
          head: t("project.searcherSpace"),
          sub: [
            { name: t("project.searchAlert"), url: "/serach-alert" },
            { name: t("project.propertiesFollowed"), url: "/followed-properties" },
            {
              name: t("project.interactedProperties"),
              url: "/properties?favourites=true",
            },
            { name: t("project.renterApplicationFile"), url: "/renter-file" },
            { name: t("project.buyerFile"), url: "/buyer-file" },
            {
              name: t("project.manageTransaction"),
              url: "/real-estate-transaction-searcher",
            },
          ],
        },
        {
          head: t("project.ownerSpace"),
          sub: [
            { name: t("project.myProperty"), url: "/my-properties" },
            { name: t("buttons.listProperty"), url: "/property1" },
            { name: t("project.sellerFile"), url: "/seller-file" },
            {
              name: t("project.manageTransaction"),
              url: "/real-estate-transaction-owner",
            },
          ],
        },
      ],
    },
  ];
  // const getNotifications = () => {
  //   const dto = {
  //     sendToId: user?._id,
  //   };
  //   loader(true);
  //   ApiClient.get("notification/list", dto).then((res) => {
  //     if (res.success) {
  //       let unreadLen =
  //         res?.data?.filter((ee) => ee.status != "read")?.length || "";
  //       setNotLength(unreadLen);
  //     }
  //     loader(false);
  //   });
  // };
  // useEffect(() => {
  //   if (user?.loggedIn) getNotifications();
  // }, []);
  useEffect(() => {
    // Recharge le profil + le plan actif dès qu'un utilisateur est authentifié
    // (token présent). On ne se fie PAS au seul flag `user.isGuest` : il reste
    // parfois à `true` après connexion (hydratation de token), ce qui empêchait
    // le rafraîchissement du plan et laissait des droits périmés en cache
    // (ex. coach IA affiché « inclus » alors que le plan ne l'inclut pas).
    const userId = user?._id || user?.id;
    const validUserId = userId && /^[a-f0-9]{24}$/i.test(String(userId));
    if (isDebugMockUser() || isGuestMode() || !validUserId) return;
    ApiClient.get(`user/detail`, { id: userId }).then((res) => {
      if (res.success) {
        dispatch(login_success(res?.data));
      }
    });
    ApiClient.get("user/activeplan", { userId }).then((res) => {
      if (res.success) {
        dispatch(active_plan_success(res?.data));
      }
    });
  }, [user?._id]);

  // Hydratation du user depuis le token : si un token existe mais que l'état
  // Redux n'a pas (encore) de user connecté (ex. juste après l'inscription, ou
  // rechargement avec un état persisté incomplet), on recharge le profil pour
  // afficher le chrome InApp (sidebar/header). Sans cela, la sidebar restait
  // masquée car l'appel `user/detail` est conditionné à `user.loggedIn`.
  useEffect(() => {
    if (isDebugMockUser() || user.loggedIn) return;
    let token = null;
    try {
      token = window.localStorage.getItem("token") || window.localStorage.getItem("access_token");
    } catch (e) { /* ignore */ }
    if (!token) return;
    let id = null;
    try {
      const payload = JSON.parse(window.atob(token.split(".")[1] || ""));
      id = payload.id || payload._id || payload.userId || null;
    } catch (e) { /* ignore */ }
    if (!id) return;
    ApiClient.get("user/detail", { id }).then((res) => {
      if (res?.success && res?.data) {
        dispatch(login_success(res.data));
      }
    }).catch(() => {});
  }, [user.loggedIn]);

  // Rattachement par tracking de session : dès qu'un utilisateur est authentifié,
  // ses estimations réalisées en invité (même session navigateur) lui sont liées.
  useEffect(() => {
    if (!isDebugMockUser() && user.loggedIn && !user.isGuest) {
      try {
        const sid = getGuestSessionId();
        if (sid) {
          ApiClient.post("estimation/claim", { sessionId: sid }).catch(() => {});
        }
      } catch (e) { /* ignore */ }
    }
  }, [user.loggedIn, user.isGuest]);
  useEffect(() => {
    if (pathname === "/chat") {
      setIsInChatPage(true);
    } else {
      setIsInChatPage(false);
    }
    if (user.loggedIn && !user.isGuest) {
      notificationListener(navigate, setNotLength);
    }
  }, [location]);
  // Listen for the 'notify-message' event from socket
  useEffect(() => {
    const onMessageNotification = (res) => {
      if (res.status === 200) {
        const newmsg = res.data.count_room_chat;
        if (!isInChatPage) {
          setChatLength((prev) => newmsg);
          // setChatLength((prev) => (prev < 9 ? prev + 1 : 9));
        }
      }
    };
    socket.on("notify-message", onMessageNotification);
    return () => {
      socket.off("notify-message", onMessageNotification);
    };
  }, [isInChatPage, socket]);

  const dropdownRef = useRef(null);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setTimeout(() => {
          setProjectData("");
        }, 100);
      }
    };
    socket.emit("un-noti", {
      user_id: user?.id || user?._id,
    });
    socket.on("un-noti", (res) => {
      setNotLength(res?.data?.unread_count);
    });

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const getAllProperty = () => {
    const userId = user?.id || user?._id;
    if (!userId) {
      setpropertyLoader(false);
      return;
    }

    setpropertyLoader(true);
    // Comptage léger : `property/count` (total seul) au lieu de charger 1000
    // documents complets via `property/listing?count=1000` pour ne lire que `total`.
    ApiClient.get(`property/count?status=active&addedBy=${userId}`)
      .then((res) => {
        if (res.success) {
          setpropertyTotal(res.total);
        }
      })
      .catch(() => {
        setpropertyTotal(0);
      })
      .finally(() => {
        setpropertyLoader(false);
      });
  };

  useEffect(() => {
    if (user.loggedIn && !user.isGuest) {
      getAllProperty();
    }
  }, []);

  const handleProperty = () => {
    if (user.loggedIn) {
      if (propertyTotal >= activePlan?.activePlan?.[0]?.numberOfProperty) {
        openUpgradeModal({ action: "create_property" });
        return;
      }
      removePropData();
      return navigate("/property1");
    } else {
      setloginModal(true);
    }
  };


  // Injection du menu utilisateur dans window pour accès depuis Html.jsx
  window.renderAccountMenu = () => accountMenu[0].menu;

  // Barre de navigation inférieure (mobile, environnements InApp)
  // Pour les comptes non-pro (et invités) : entrée « Services immo à la carte »
  // (Marketplace) après « Rechercher ».
  const hasProsContent = user?.accountType === "pro" || !!user?.agencyOwnerId;
  const bottomNavItems = [
    { label: t("inappNav.home", "Accueil"), icon: LuHouse, to: "/app-home", active: pathname === "/app-home" || pathname.startsWith("/app-home/") },
    { label: t("inappNav.parcours", "Parcours immo"), icon: LuRoute, to: "/transaction-dashboard", active: pathname === "/transaction-dashboard" || pathname.startsWith("/transaction-dashboard/") },
    { label: t("inappNav.search", "Rechercher"), icon: LuSearch, to: "/properties", active: pathname === "/properties" || pathname.startsWith("/properties/") },
    ...(!hasProsContent ? [
      { label: t("inappNav.services", "Services"), icon: LuStore, to: "/marketplace", active: pathname === "/marketplace" || pathname.startsWith("/marketplace/") },
    ] : []),
    { label: t("inappNav.messages", "Messages"), icon: LuMessagesSquare, to: "/chat", active: pathname === "/chat" || pathname.startsWith("/chat/") },
    { label: t("inappNav.assistant", "Assistant IA"), icon: LuSparkles, to: "/app-assistant", active: pathname === "/app-assistant" || pathname.startsWith("/app-assistant/") },
  ];
  const BOTTOM_NAV_HIDDEN_PREFIXES = [
    "/property-details", "/annonce",
    "/property1", "/property3", "/property/add", "/property/edit",
  ];
  // Détail d'un service marketplace (/marketplace/:id) : on masque la barre
  // d'onglets pour ne pas perturber l'acte d'achat (paiement en footer sticky).
  const isMarketplaceServiceDetail =
    /^\/marketplace\/[^/]+\/?$/.test(pathname) &&
    !["/marketplace/favorites", "/marketplace/orders"].includes(pathname);
  // Écrans de saisie plein écran avec pied sticky Annuler/Enregistrer :
  // on masque la barre d'onglets pour laisser la place au pied sticky.
  const isStickyFooterRoute =
    pathname === "/estimation" ||
    pathname === "/mes-recherches/publier" ||
    /^\/mes-recherches\/[^/]+\/modifier\/?$/.test(pathname) ||
    /^\/(buyer|renter)-file\/synthese\/[^/]+\/?$/.test(pathname) ||
    /^\/dossier-visite\/generer\/[^/]+\/?$/.test(pathname);
  const showBottomNav =
    (isMobileView || isTablet) &&
    !isMarketplaceServiceDetail &&
    !isStickyFooterRoute &&
    !BOTTOM_NAV_HIDDEN_PREFIXES.some((p) => pathname === p || pathname.startsWith(p));
  // Pages pleine largeur avec fond coloré : la marge est portée par la page
  // (elle doit garder son fond jusqu'en bas), pas par le conteneur gris.
  const NO_NAV_PADDING_PREFIXES = ["/revons-plus-grand", "/prolist"];
  const withBottomNavPadding =
    showBottomNav &&
    !NO_NAV_PADDING_PREFIXES.some((p) => pathname === p || pathname.startsWith(p));

  // Masque le widget Tawk.to (chat de conversion des leads) pour les
  // utilisateurs connectés. Il ne reste affiché que pour les visiteurs non
  // connectés (mode invité / non logged) à qui il sert à les convertir.
  // Sur les écrans de détail d'un bien, on le masque aussi en mode invité :
  // le widget entrerait en collision avec le footer sticky mobile.
  // Idem dès que la barre de navigation inférieure mobile (footer sticky) est
  // affichée (web mobile comme app native) : le widget entrerait en collision.
  // Tawk_API peut être indisponible au premier rendu (script chargé en async),
  // on ré-applique la consigne régulièrement jusqu'à ce que l'API réponde.
  const isPropertyDetailsRoute =
    pathname.startsWith("/property-details") || pathname.startsWith("/annonce/");
  useEffect(() => {
    const apply = () => {
      try {
        const tw = typeof window !== 'undefined' ? window.Tawk_API : null;
        if ((isLoggedIn && !isGuest) || isPropertyDetailsRoute || isApp() || showBottomNav) {
          tw && typeof tw.hideWidget === 'function' && tw.hideWidget();
        } else {
          tw && typeof tw.showWidget === 'function' && tw.showWidget();
        }
      } catch (e) { /* ignore */ }
    };
    apply();
    const timer = window.setInterval(apply, 1500);
    // Renfort CSS : masque l'iframe Tawk sous le body dès qu'un user est connecté
    try {
      if (document.body) {
        document.body.classList.toggle('anyhomes-user-logged', !!(isLoggedIn && !isGuest) || isPropertyDetailsRoute || isApp() || showBottomNav);
      }
    } catch (e) { /* ignore */ }
    return () => window.clearInterval(timer);
  }, [isLoggedIn, isGuest, isPropertyDetailsRoute, showBottomNav]);

  // Bouton retour global (app native) sur les pages de détail.
  const BACK_ROUTES = [
    "/annonce",
    "/property-details",
    "/property-timeline",
    "/followed-properties-list",
    "/blog-own-detail",
    "/video-own-detail",
    "/marketplace/",
    "/learning-center",
  ];
  // Web mobile + app native : sur une annonce, on masque le header pour laisser
  // l'image du bien plein écran jusqu'à la barre d'état.
  const isPropertyAnnounceDetail =
    pathname.startsWith("/annonce/") || pathname.startsWith("/property-details");
  // Recherche de biens : sur mobile (web + iOS), on masque le header global
  // (hamburger/logo/alerte/compte) au profit d'une zone de recherche dédiée.
  const isPropertySearchPage = pathname === "/properties" || pathname.startsWith("/properties/");
  const hideHeaderMobile = isMobileView && (isPropertyAnnounceDetail || isPropertySearchPage);

  const showBackButton =
    isApp() && !hideHeaderMobile && BACK_ROUTES.some((p) => pathname === p || pathname.startsWith(p));

  return (
    <>
      <LoginModal loginModal={loginModal} setloginModal={setloginModal} />
      <UpgradePlan planModal={planModal} setplanModal={setplanModal} />
      <OnboardingCompletionModal
        isOpen={showCompletionModal}
        markCelebrationSeen={markCelebrationSeen}
        onClose={() => {}}
        onOpenDashboard={() => navigate('/dashboard')}
        onShareAnyHomes={() => navigate('/invite?source=celebration_modale')}
      />
      <div 
        component="page-layout" 
        className="page-layout-root"
        style={{
          '--sidebar-width': shouldShowSidebar && !isMobileView 
            ? (isSidebarOpen ? '280px' : '70px')
            : '0px'
        }}
      >
        {/* Sidebar gauche — masquée sur mobile, visible sur tablet/desktop */}
        {shouldShowSidebar && !isMobileView && (
          <aside className={`sidebar-container ${!isSidebarOpen ? 'collapsed' : ''}`}>
            <Sidebar isOpen={isSidebarOpen} onToggle={toggleSidebar} propertyTotal={propertyTotal} />
          </aside>
        )}
        <div className="flex-1 min-w-0">
          {/* Header en haut */}
          {!hideHeaderMobile && (
            <Header
              setIsOpen={setIsSidebarOpen}
              isOpen={isSidebarOpen}
              particularData={null}
              showAccountMenu={showAccountMenu}
              setShowAccountMenu={setShowAccountMenu}
              isMobileView={isMobileView}
              hasSidebar={shouldShowSidebar}
              onOpenMobileSidebar={() => setIsMobileSidebarOpen(true)}
            />
          )}
          {/* Annonce mobile : layer blanc translucide sous l'heure / la batterie
              (l'image du bien reste visible en dessous). PAS sur /properties :
              la barre de recherche y a déjà son propre fond blanc et ce layer
              recouvrirait le champ + le bouton « recherches récentes ». */}
          {hideHeaderMobile && isPropertyAnnounceDetail && (
            <div
              aria-hidden="true"
              className="lg:hidden fixed top-0 left-0 right-0 z-[60] pointer-events-none"
              style={{
                height: "calc(var(--safe-top, env(safe-area-inset-top, 0px)) + 12px)",
                background: "rgba(255,255,255,0.6)",
              }}
            />
          )}
          {/* Bouton retour global (app native) — pages de détail */}
          {showBackButton && (
            <MobileBackButton
              className="lg:hidden"
              style={{ position: 'fixed', top: 'calc(71px + var(--safe-top, env(safe-area-inset-top, 0px)) + 8px)', left: 12, zIndex: 45 }}
            />
          )}
          {/* Main Content Area */}
          <TrialBanner />
          <main
            className={`page-content-wrapper ${
              shouldShowSidebar && !isMobileView
                ? isSidebarOpen ? 'with-sidebar sidebar-expanded' : 'with-sidebar sidebar-collapsed'
                : 'full-width'
            }${withBottomNavPadding ? ' with-bottom-nav' : ''}${hideHeaderMobile ? ' no-header' : ''}`}
          >
            <div className="pageContent">{children}</div>
          </main>
          {/* Barre de navigation inférieure mobile (InApp) */}
          {showBottomNav && (
            <>
            <nav
              aria-label={t("inappNav.label", "Navigation principale")}
              className={`fixed bottom-[max(env(safe-area-inset-bottom),10px)] z-[70] mx-auto ${isTablet ? "max-w-2xl" : "max-w-md"} rounded-full bg-white/65 backdrop-blur-md border border-[#d9d9e0] shadow-[0_4px_22px_rgba(0,0,0,0.14)] flex items-stretch px-1.5 py-2`}
              style={{ left: 'calc(var(--sidebar-width, 0px) + 12px)', right: 12 }}
            >
              {/* Excroissance « créer un bien » intégrée à la forme du footer :
                  même surface translucide, sans bord bas → le demi-cercle est un
                  prolongement de la pilule (plus d'effet « forme superposée »). */}
              <button
                type="button"
                onClick={() => {
                  const loggedIn = !!user?.loggedIn && !user?.isGuest && !isGuestMode();
                  if (!loggedIn) return openLoginModal();
                  navigate('/property1');
                }}
                title={t("inappNav.createProperty", "Créer un bien")}
                aria-label={t("inappNav.createProperty", "Créer un bien")}
                className="create-property-btn absolute left-1/2 -translate-x-1/2 w-14 h-8 rounded-t-full bg-white/65 backdrop-blur-md border border-[#d9d9e0] border-b-0 flex items-center justify-center text-[var(--brand,#976DD0)]"
                style={{ bottom: '100%' }}
              >
                <LuPlus size={20} strokeWidth={2.4} />
              </button>
              {bottomNavItems.map((item) => (
                <button
                  key={item.to}
                  type="button"
                  onClick={() => navigate(item.to)}
                  title={item.label}
                  aria-label={item.label}
                  className={`flex flex-col items-center justify-center gap-1 flex-1 rounded-full px-0.5 transition-colors ${
                    item.active ? "text-[var(--brand,#976DD0)]" : "text-[#343F4B] hover:text-[var(--brand,#976DD0)]"
                  }`}
                >
                  <item.icon size={22} strokeWidth={1.9} />
                  <span className={`text-[12px] font-semibold leading-none ${isTablet ? "whitespace-nowrap" : ""} ${item.active ? "" : "text-[#5b5b61]"}`}>
                    {item.label}
                  </span>
                </button>
              ))}
            </nav>
            </>
          )}
          {/* Overlay mobile — plein écran, déclenché par le hamburger du header.
              z-index élevé : le menu doit passer AU-DESSUS de la barre d'onglets
              mobile (z-[70]) et du bouton « créer un bien » (z-[80]). */}
          {shouldShowSidebar && isMobileView && (
            <Transition show={isMobileSidebarOpen} as={Fragment}>
              <Dialog as="div" className="relative z-[100]" onClose={() => setIsMobileSidebarOpen(false)}>
                <Transition.Child as={Fragment} enter="ease-in-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in-out duration-300" leaveFrom="opacity-100" leaveTo="opacity-0">
                  <div className="fixed inset-0 bg-black bg-opacity-20" />
                </Transition.Child>
                <div className="fixed inset-0 overflow-hidden">
                  <div className="absolute inset-0 overflow-hidden">
                    <Transition.Child as={Fragment} enter="transform transition ease-in-out duration-300" enterFrom="-translate-x-full" enterTo="translate-x-0" leave="transform transition ease-in-out duration-300" leaveFrom="translate-x-0" leaveTo="-translate-x-full">
                      <Dialog.Panel className="relative h-full shadow-xl bg-white" style={{ background: '#ffffff', width: '280px', maxWidth: '90vw' }}>
                        <Sidebar isOpen={true} onToggle={() => setIsMobileSidebarOpen(false)} propertyTotal={propertyTotal} />
                      </Dialog.Panel>
                    </Transition.Child>
                  </div>
                </div>
              </Dialog>
            </Transition>
          )}
        </div>
      </div>
    </>
  );
};
export default PageLayout;
